import React, { useEffect, useState } from 'react';

import Button from '../../../../components/Button';
import Loader from '../../../../components/Loader';
import useEmpDailyTask from '../useDailyTask';
import JibbleModal from '../../../../components/Header/JibbleModal';
import ApiService from '../../../../service/ApiService';
import { apiPaths } from '../../../../service/apiPaths';
import toast from 'react-hot-toast';

const EmpDailyTask: React.FC = () => {
  const {
    loading,
    data,
    fetchData,
    setDate,
    formData,
    setFormData,
    handleSubmit,
    localTaskHours,
    handleHoursChange,
    submitHours,
    calculateTotalHoursAndMinutes,
  } = useEmpDailyTask();
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [hasFetchedData, setHasFetchedData] = useState<boolean>(false);
  const [hours, setHours] = useState<Number>(0);
  const [isJibbleModalOpen, setIsJibbleModalOpen] = useState<boolean>(false);

  // Helper function to check if a task is overdue
  const isOverdueTask = (task: any): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const createdAt = new Date(task.createdAt);
    createdAt.setHours(0, 0, 0, 0);
    
    const dueDate = new Date(task.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    // Task is overdue if:
    // 1. Created date is before today (task was created in the past)
    // 2. Due date is today or in the past
    // 3. Status is not finished or backLog
    // This means the task was carried forward from a previous day
    return (
      createdAt < today && 
      dueDate <= today && 
      !["finished", "backLog"].includes(task.status)
    );
  };

  useEffect(() => {
    const fetchDataAndSubmit = async () => {
      setDate(selectedDate);
      if (!hasFetchedData) {
        await handleSubmit(false); // Don't send Slack message on page load
        await fetchData();
        setHasFetchedData(true);
      }
    };
    fetchDataAndSubmit();
  }, [selectedDate, hasFetchedData]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setDate(newDate);
    setHasFetchedData(false);
  };

  const handleFormDataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleJibbleStatusSubmit = async (status: 'IN' | 'OUT', activityId?: string, note?: string) => {
    // Ensure note is "Day Off" when status is "OUT"
    const finalNote = status === 'OUT' ? 'Day Off' : (note || undefined);
    
    try {
      // First, check the current Jibble status from the server
      let currentServerStatus: 'IN' | 'OUT' | null = null;
      try {
        const res = await ApiService({
          method: 'GET',
          endpoint: apiPaths.getJibbleStatus,
        });
        if (res) {
          const rawType: string = (res.type || res?.status?.name || '').toString();
          const normalized = rawType.trim().toUpperCase();
          currentServerStatus = normalized.includes('IN') ? 'IN' : 'OUT';
        }
      } catch (fetchError) {
        // If we can't fetch status, proceed with the API call anyway
        console.warn('Could not fetch current Jibble status:', fetchError);
      }
      
      // If trying to set OUT and already OUT on server, skip the API call
      if (status === 'OUT' && currentServerStatus === 'OUT') {
        toast('You are already clocked out', { icon: 'ℹ️' });
        // Still update localStorage and header to ensure consistency
        localStorage.setItem('jibbleStatusSelection', status);
        if (finalNote && finalNote.trim()) {
          localStorage.setItem('jibbleNote', finalNote.trim());
        } else {
          localStorage.removeItem('jibbleNote');
        }
        window.dispatchEvent(new CustomEvent('jibbleStatusChanged', { detail: { status, note: finalNote } }));
      } else {
        // Call jibbleStatus API when user selects Yes
        try {
          await ApiService({
            method: 'POST',
            endpoint: apiPaths.jibbleStatus,
            data: {
              status_type: status,
              ...(status === 'IN' && activityId ? { status_id: activityId } : {}),
              ...(finalNote && finalNote.trim() ? { note: finalNote.trim() } : {}),
            },
          });
          toast.success(`Jibble ${status} recorded`);
          
          // Update localStorage to reflect status change in header
          localStorage.setItem('jibbleStatusSelection', status);
          if (finalNote && finalNote.trim()) {
            localStorage.setItem('jibbleNote', finalNote.trim());
          } else {
            localStorage.removeItem('jibbleNote');
          }
          
          // Dispatch custom event to notify header component
          window.dispatchEvent(new CustomEvent('jibbleStatusChanged', { detail: { status, note: finalNote } }));
        } catch (apiError: any) {
          // Handle specific error for invalid_interval_out_out
          const errorCode = apiError?.response?.data?.code;
          const errorMessage = apiError?.response?.data?.message || 'Failed to update Jibble status';
          
          if (errorCode === 'invalid_interval_out_out') {
            // User is already OUT, so we can consider this as desired state
            toast('You are already clocked out', { icon: 'ℹ️' });
            // Still update localStorage to ensure consistency
            localStorage.setItem('jibbleStatusSelection', status);
            if (finalNote && finalNote.trim()) {
              localStorage.setItem('jibbleNote', finalNote.trim());
            }
            window.dispatchEvent(new CustomEvent('jibbleStatusChanged', { detail: { status, note: finalNote } }));
          } else {
            toast.error(errorMessage);
            throw apiError; // Re-throw if it's a different error
          }
        }
      }
    } catch (err: any) {
      // Only show error if it's not the invalid_interval_out_out error (already handled above)
      const errorCode = err?.response?.data?.code;
      if (errorCode !== 'invalid_interval_out_out') {
        toast.error(err?.response?.data?.message || 'Failed to update Jibble status');
      }
    } finally {
      setIsJibbleModalOpen(false);
      // Always call handleSubmit (createEmpDailyTasks) after modal closes, regardless of Jibble API result
      // Send Slack message when user explicitly clicks Save Updates
      await handleSubmit(true);
    }
  };

  const handleJibbleModalClose = async () => {
    setIsJibbleModalOpen(false);
    // Always call handleSubmit (createEmpDailyTasks) when modal closes (user clicks No)
    // Send Slack message when user explicitly clicks Save Updates
    await handleSubmit(true);
  };

  const handleSaveUpdatesClick = () => {
    setIsJibbleModalOpen(true);
  };

  return (
    <div className="h-[87vh] p-3 rounded-lg bg-white overflow-y-scroll">
      <h6 className="text-lg font-semibold text-fontBlack">
        Daily End of Day (EOD) Report
      </h6>

      <div className="m-2 flex justify-between">
        <div>
          <label htmlFor="startDate">Select Date:</label>
          <input
            type="date"
            id="startDate"
            value={selectedDate}
            onChange={handleDateChange}
            className="border p-2 ml-2"
            disabled
          />
        </div>
        <div>
          {data.length > 0 && (
            <div className="text-red-500">
              <span>
                {"Tracked Hours = "}
                {calculateTotalHoursAndMinutes().displayHours}
                {":"}
                {calculateTotalHoursAndMinutes().displayMinutes}
              </span>
            </div>
          )}
          <Button
            onClick={handleSaveUpdatesClick}
            className="px-4 py-2 mb-3 bg-blue-500 disabled:bg-blue-200 text-white rounded"
            type="submit"
            disabled={
              formData.tomorrowUpdate && formData.todayUpdate ? false : true
            }
          >
            Save Updates
          </Button>
        </div>
      </div>

      <div className="flex justify-between mb-4">
        <div className="w-1/2 pr-2 -mt-6">
          <h3 className="font-semibold">
            Today Worked On:
            <h3 className="text-red-500">
              {`1. What you have done today? 
          2. Do you have any dependency?`}
            </h3>
          </h3>

          <textarea
            name="todayUpdate"
            placeholder="Enter today's tasks..."
            value={formData.todayUpdate}
            onChange={handleFormDataChange}
            className="border p-2 rounded w-full bg-gray-100"
            rows={4}
          />
        </div>
        <div className="w-1/2 pl-2">
          <h3 className="font-semibold">Next Working day plan:</h3>
          <textarea
            name="tomorrowUpdate"
            placeholder="Enter tomorrow's tasks..."
            value={formData.tomorrowUpdate}
            onChange={handleFormDataChange}
            className="border p-2 rounded w-full bg-gray-100"
            rows={4}
          />
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="space-y-6">
          {data.length > 0 ? (
            data.map((taskData: any) => (
              <div key={taskData._id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <p className="text-sm text-gray-600">
                    Date: {new Date(taskData.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="overflow-y-auto">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-base font-bold text-gray-700 w-1/3">Task</th>
                        <th className="px-4 py-3 text-left text-base font-bold text-gray-700 w-1/6">Status</th>
                        <th className="px-4 py-3 text-left text-base font-bold text-gray-700 w-1/6">Project</th>
                        <th className="px-4 py-3 text-left text-base font-bold text-gray-700 w-1/6">Priority</th>
                        <th className="px-4 py-3 text-left text-base font-bold text-gray-700 w-1/6">Hours</th>
                        <th className="px-4 py-3 text-left text-base font-bold text-gray-700 w-1/6">Due Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 border-b">
                      {taskData.todayTaskIds.map((task: any) => (
                        <tr key={task._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-base text-gray-700 break-words">
                            <div className="flex flex-col gap-2">
                              {/* Overdue Label */}
                              {isOverdueTask(task) && (
                                <div className="inline-flex">
                                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded font-semibold">
                                    OVERDUE
                                  </span>
                                </div>
                              )}
                              
                              {/* Task Description */}
                              <div>
                                {task.description
                                  .split(" ")
                                  .map((word: string, index: number) => {
                                    if (word.includes("http")) {
                                      return (
                                        <span key={index}>
                                          {index > 0 && " "}  
                                          <a
                                            href={word}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                          >
                                            {word}
                                          </a>
                                        </span>
                                      );
                                    }
                                    return (
                                      <span key={index}>
                                        {index > 0 && " "}
                                        {word}
                                      </span>
                                    );
                                  })}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-base text-gray-700">{task.status}</td>
                          <td className="px-4 py-3 text-base text-gray-700">{task.projectId?.name}</td>
                          <td className={`px-4 py-3 text-base font-medium ${
                            task.priority === "high" ? "text-red-600" : "text-green-600"
                          }`}>
                            {task.priority}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <select
                                value={Math.floor(localTaskHours[task._id] ?? task.hours)}
                                onChange={(e) => {
                                  const hours = Number(e.target.value);
                                  const minutes = ((localTaskHours[task._id] ?? task.hours) % 1) * 60;
                                  handleHoursChange(task._id, hours + minutes / 60);
                                }}
                                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                {Array.from({ length: 13 }, (_, i) => (
                                  <option key={i} value={i}>
                                    {i}
                                  </option>
                                ))}
                              </select>
                              <span className="text-gray-500">:</span>
                              <select
                                value={Math.round(((localTaskHours[task._id] ?? task.hours) % 1) * 60)}
                                onChange={(e) => {
                                  const minutes = Number(e.target.value);
                                  const hours = Math.floor(localTaskHours[task._id] ?? task.hours);
                                  handleHoursChange(task._id, hours + minutes / 60);
                                }}
                                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                {Array.from({ length: 60 }, (_, i) => (
                                  <option key={i} value={i}>
                                    {i.toString().padStart(2, "0")}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <div className="flex flex-col gap-1">
                              <span>{new Date(task.dueDate).toLocaleDateString()}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>  
                <div className="flex justify-between p-4 mt-4">
                  {data.length > 0 && (
                    <div className=" ">
                      <h4 className="font-semibold">Total Time Worked:</h4>
                      <p>
                        {calculateTotalHoursAndMinutes().displayHours} hours{" "}
                        {calculateTotalHoursAndMinutes().displayMinutes} minutes
                      </p>
                    </div>
                  )}
                  <Button
                    onClick={() => {
                      for (const [taskId, updatedHours] of Object.entries(
                        localTaskHours
                      )) {
                        submitHours(taskId, updatedHours);
                      }
                    }}
                    className="px-4 py-2 mb-3 h bg-blue-500 disabled:bg-blue-200 text-white rounded"
                    type="submit"
                  >
                    Save Time
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p>No tasks available for the selected date.</p>
          )}
        </div>
      )}
      <JibbleModal
        open={isJibbleModalOpen}
        onClose={handleJibbleModalClose}
        onSubmit={handleJibbleStatusSubmit}
        defaultStatus="OUT"
        confirmationMode={true}
        confirmationMessage="Are you sure you want to set Jibble status to OUT?"
      />
    </div>
  );
};

export default EmpDailyTask;